// material
import { Box, Button, Card, Container, Stack, Typography, Link, Fade } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// router
import { useNavigate, useParams } from 'react-router-dom';
// GRAPHQL
import { useQuery, useMutation } from '@apollo/client';
// import { UserGQLFunction } from '../graphql/USER';
import { RegisterUserGQLFunction } from '../graphql/REGISTER_USER';
// hooks
import useResponsive from '../hooks/useResponsive';
// components
import Page from '../components/Page';
import BasicBreadcrumb from '../components/BasicBreadcrumb';
import LoadingInfo from '../sections/people/LoadingInfo';
import KeyValueDisplay from '../components/KeyValueDisplay';
// import KeyValuesDisplay from '../components/KeyValuesDisplay';
import KeyValueLabelDisplay from '../components/KeyValueLabelDisplay';

// info
// import { USER } from '../info/USER';
import { REGISTER_USER } from '../info/REGISTER_USER';

const RootStyle = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center'
}));
const ContentStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  // maxWidth: '50%',
  margin: theme.spacing(2),
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  padding: theme.spacing(2)
}));

const DataDisplay = ({ data, smUp, t }) => (
  <Box
    sx={{
      display: 'grid',
      columnGap: 2,
      rowGap: 3,
      gridTemplateColumns: smUp ? 'repeat(2, 1fr)' : '1fr',
      m: 1,
      mb: 4
    }}
  >
    <KeyValueDisplay title={t('common.user_id')} data={data.registerUserInfo.userId} />
    <KeyValueDisplay title={t('common.email')} data={data.registerUserInfo.authenticator} />
    <KeyValueDisplay title={t('common.username')} data={data.registerUserInfo.username} />
    <KeyValueDisplay title={t('common.nickname')} data={data.registerUserInfo.nickname || '-'} />
    <KeyValueDisplay title={t('common.title')} data={data.registerUserInfo.title || '-'} />
    <KeyValueDisplay title={t('people.auth_code')} data={data.registerUserInfo.authCode} />
    <KeyValueLabelDisplay
      title={t('common.user_type')}
      data={t(`common.${REGISTER_USER.USER_TYPE[data.registerUserInfo.userType]}`)}
      color={'default'}
    />
  </Box>
);

export default function RegisterUserInfo() {
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const { registerUserId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { loading: queryLoading, data: queryData } = useQuery(RegisterUserGQLFunction.GET_REGISTER_USER_INFO, {
    variables: { id: registerUserId },
    fetchPolicy: 'network-only'
  });

  const [deleteUser] = useMutation(RegisterUserGQLFunction.DELETE_REGISTER_USER, {
    variables: { id: registerUserId },
    onCompleted: () => {
      enqueueSnackbar(t('common.success'), { variant: 'success' });
      navigate('/register', { replace: true });
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const [sendEmailAuth] = useMutation(RegisterUserGQLFunction.SEND_EMAIL_AUTH, {
    variables: { id: registerUserId },
    onCompleted: () => {
      enqueueSnackbar(t('common.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const bread = [
    <Link href="/register" underline="hover" key="1" color="inherit">
      {t('nav.enroll')}
    </Link>,
    <Typography key="2" color="text.primary">
      {t('people.info_title', { identity: t('nav.enroll') })}
    </Typography>
  ];

  const identityName = t('nav.enroll');

  return (
    <Page title={t('people.info_title', { identity: identityName })}>
      <Container maxWidth={false}>
        <Stack direction="column">
          <Typography variant="h4" gutterBottom>
            {t('people.enroll_info_title')}
          </Typography>
          <BasicBreadcrumb bread={bread} />
        </Stack>

        <RootStyle>
          <ContentStyle>
            {!queryLoading && queryData ? <DataDisplay data={queryData} smUp={smUp} t={t} /> : <LoadingInfo smUp={smUp} />}

            <Stack direction={smUp ? 'row' : 'column'} justifyContent="space-between" alignItems="center" spacing={2}>
              <Button
                sx={{ maxWidth: smUp ? '200px' : '100%' }}
                fullWidth
                variant="contained"
                onClick={() => {
                  navigate(`/register/info/edit/${registerUserId}`);
                }}
              >
                {t('common.edit')}
              </Button>
              <Button
                sx={{ maxWidth: smUp ? '200px' : '100%' }}
                fullWidth
                variant="outlined"
                onClick={() => {
                  const userId = queryData?.registerUserInfo?.userId;
                  if (userId) {
                    navigate(`/user/history?userId=${userId}&id=${registerUserId}&from=register`);
                  }
                }}
              >
                {t('history.title')}
              </Button>
              <Button
                sx={{ maxWidth: smUp ? '200px' : '100%' }}
                fullWidth
                variant="outlined"
                onClick={() => {
                  confirm({
                    title: t('people.send_auth_title'),
                    description: t('people.send_auth_desc'),
                    confirmationText: t('people.send_auth_btn'),
                    cancellationText: t('common.cancel'),
                    confirmationButtonProps: { color: 'info' }
                  })
                    .then(() => {
                      sendEmailAuth();
                    })
                    .catch(() => {
                      enqueueSnackbar(t('people.send_auth_error'), {
                        TransitionComponent: Fade,
                        preventDuplicate: true,
                        variant: 'warning'
                      });
                    });
                }}
              >
                {t('people.send_auth_btn')}
              </Button>
              <Button
                sx={{ maxWidth: smUp ? '200px' : '100%' }}
                fullWidth
                variant="outlined"
                color="error"
                onClick={() => {
                  confirm({
                    title: t('people.delete_confirm_title', { identity: identityName }),
                    description: t('people.delete_confirm_desc', { identity: identityName }),
                    confirmationText: t('common.delete'),
                    cancellationText: t('common.cancel'),
                    confirmationButtonProps: { color: 'error' }
                  })
                    .then(() => {
                      deleteUser();
                    })
                    .catch(() => {
                      enqueueSnackbar(t('people.delete_cancel', { identity: identityName }), {
                        TransitionComponent: Fade,
                        preventDuplicate: true,
                        variant: 'warning'
                      });
                    });
                }}
              >
                {t('common.delete')}
              </Button>
            </Stack>
          </ContentStyle>
        </RootStyle>
      </Container>
    </Page>
  );
}
